import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { renderToString } from 'react-dom/server';
import ProgramCell from '@/components/admin/ProgramAccess/ProgramAccessTable/ProgramCell';
import { ISeatPool, ISubscriber, IUserType } from '@/components/admin/ProgramAccess/ProgramAccessTable/utils';
import ProgramActionsDropdown from '../ProgramActionsDropdown';
import GradeDropdown from './GradeDropdown';
import ProgramCode from './ProgramCode';

const getColumnDefs = (
  subscriber: ISubscriber,
  userType: IUserType,
) => {
  const baseColumnDefs = [
    {
      className: 'tw-align-middle',
      createdCell: (td, _cellData, rowData) => {
        const exclusionSetting = rowData.exclusion_setting.replaceAll('_', '-');
        if (exclusionSetting === 'no-exclusions') return;
        // .exclude-from-rostering-row, .remove-all-students-row classes from stylesheets/_program_home.scss
        td.parentElement.classList.add(`${exclusionSetting}-row`);
      },
      render: (_data: undefined, _type, row: ISeatPool) => (
        row.program.licenses.find(license => license.id === row.license_id).name
      ),
      searchable: false,
      sortable: false,
      targets: 0,
      width: '16%',
    },
    {
      className: 'tw-align-middle',
      createdCell: (td, _cellData, rowData: ISeatPool) => {
        ReactDOM.render(<GradeDropdown seatPool={rowData} userType={userType} />, td);
      },
      searchable: false,
      sortable: false,
      targets: 1,
    },
    {
      className: 'tw-align-middle',
      render: (_data, _type, row: ISeatPool) => (
        row.program && renderToString(<ProgramCell program={row.program} />)
      ),
      searchable: false,
      sortable: false,
      targets: 2,
      width: '42%',
    },
    {
      className: 'tw-align-middle tw-break-all',
      createdCell: (td, _cellData, rowData) => {
        ReactDOM.render(<ProgramCode rowData={rowData} />, td);
      },
      searchable: false,
      sortable: false,
      targets: 3,
      width: '20%',
    },
    {
      className: 'tw-align-middle tw-text-center',
      searchable: false,
      sortable: false,
      targets: 4,
      title: 'Teachers',
    },
    {
      className: 'tw-align-middle tw-text-center',
      searchable: false,
      sortable: false,
      targets: 5,
      title: 'Students',
    },
  ];

  if (userType === 'Coordinator') return baseColumnDefs;
  return ([
    ...baseColumnDefs,
    {
      createdCell: (td, _cellData, rowData) => {
        ReactDOM.render(
          <ProgramActionsDropdown
            isSysadmin
            exclusionSetting={rowData.exclusion_setting}
            initiallyUnderReview={rowData.under_review}
            licenseId={rowData.program.license_id}
            seatPoolId={rowData.id}
            showReviewButton={false}
            subscriberStateId={subscriber.state_id}
          />,
          td,
        );
      },
      searchable: false,
      sortable: false,
      targets: -1,
    },
  ]);
};

const useProgramAccessTableColumnDefs = (
  subscriber: ISubscriber,
  userType: IUserType,
) => (
  useMemo(() => getColumnDefs(subscriber, userType), [subscriber.id, userType])
);

export default useProgramAccessTableColumnDefs;
